import {AnyObject, ObjectFieldsAssertion, PartiallyRequired, Values} from '@wix/devzai-utils-common';
import {
    doppeValidateLinkPath,
    doppeValidateLinkUrl,
    doppeValidateReferencedExternalAssetUrl
} from '../doppe-validators/doppe-validators';
import {DoppeDtoAppFeatures, DoppeDtoAppFeaturesDefaultValues} from './doppe-dto-app-features';
import {DoppeDtoViewerFeatures} from './doppe-dto-viewer-features';
import {DoppePartners} from './doppe-partners';
import {DoppeDtoUserOnboardingUTMSource} from './doppe-dto-user-onboarding-data';
import {DoppeDtoUserOnboardingLeadData} from './doppe-dto-user-onboarding-lead-data';
import {DoppeAppBehaviourSettings} from "./doppe-app-behaviour-settings";
import {DoppeContactsMetaSite} from "./doppe-contacts-meta-site";

export type LinkNotificationData = {
    shouldShow: boolean;
    targetUrl: string;
    path: string;
    linkId: string;
}

export const HoppPageActionsBanners = {
    showCreateYourFirstLinkActionPage: 'showCreateYourFirstLinkActionPage',
    showCreateYourFirstProductActionPage: 'showCreateYourFirstProductActionPage',
    showCreateYourFirstServiceActionPage: 'showCreateYourFirstServiceActionPage',
    showCreateYourFirstBlogPostActionPage: 'showCreateYourFirstBlogPostActionPage',
    showCreateYourFirstContactFormActionPage: 'showCreateYourFirstContactFormActionPage',
    showCreateYourFirstEventActionPage: 'showCreateYourFirstEventActionPage',
    showCreateYourFirstMembershipActionPage: 'showCreateYourFirstMembershipActionPage',
    showCreateYourFirstVideoActionPage: 'showCreateYourFirstVideoActionPage',
    showCreateYourFirstFollowActionPage: 'showCreateYourFirstFollowActionPage',
    showCreateYourFirstSubscribeActionPage: 'showCreateYourFirstSubscribeActionPage',
    showCreateYourFirstDeviantArtActionPage: 'showCreateYourFirstDeviantArtActionPage',
    showCreateYourFirstCouponActionPage: 'showCreateYourFirstCouponActionPage',
} as const;

export type HoppPageActionsBanners = Values<typeof HoppPageActionsBanners>;

export const DefaultPageActionsBanners = [
    HoppPageActionsBanners.showCreateYourFirstSubscribeActionPage,
    HoppPageActionsBanners.showCreateYourFirstProductActionPage,
    HoppPageActionsBanners.showCreateYourFirstContactFormActionPage,
    HoppPageActionsBanners.showCreateYourFirstMembershipActionPage,
    HoppPageActionsBanners.showCreateYourFirstServiceActionPage,
    HoppPageActionsBanners.showCreateYourFirstEventActionPage,
    HoppPageActionsBanners.showCreateYourFirstBlogPostActionPage,
    HoppPageActionsBanners.showCreateYourFirstVideoActionPage,
    HoppPageActionsBanners.showCreateYourFirstFollowActionPage,
    HoppPageActionsBanners.showCreateYourFirstLinkActionPage,
    HoppPageActionsBanners.showCreateYourFirstCouponActionPage,
]

export interface DoppeDtoUserReadOnlySettings {
    stripeAccountId?: string;
    wixHeadlessOAuthAppId?: string;
    zapierApiKey?: string;
    zapierTriggers?: ZapierTrigger[];
    doppePartner?: DoppePartners | null;
    doppePartnerAccountId?: string | null;
    leadData?: DoppeDtoUserOnboardingLeadData | null;
    premiumModelEnabled?: boolean;
    appBehaviourSettingsOverrides?: Partial<DoppeAppBehaviourSettings>;
    contactsMetaSiteSelectionWasEnabledWhenAccountCreated?: boolean;
}

export const DoppeCookieBannerAutoSource = {
    AutoText: 'AutoText',
} as const;

export type DoppeCookieBannerAutoSource = Values<typeof DoppeCookieBannerAutoSource>;

export const DoppeCookieBannerCustomTextSource = {
    Custom: 'custom',
    AutoValue: 'autoValue',
};

export type DoppeCookieBannerCustomTextSource = Values<typeof  DoppeCookieBannerCustomTextSource>;

export interface DoppeCookieBannerSettings {
    cookieBannerEnabled?: boolean;
    customTextSource?: DoppeCookieBannerCustomTextSource;
    customTexts?: {
        consentButtonText?: string;
        settingsButtonText?: string;
        declineAllButtonText?: string;
        informText?: string;
    }
}

export interface DoppeGoogleAnalyticsSettings {
    tagId?: string;
}

export interface DoppeDtoUserUpdatableSettings {
    showLinkNotification?: LinkNotificationData,
    socialLinks?: {
        instagram?: string;
        facebook?: string;
        patreon?: string;
    };
    autoPublishPage?: boolean;
    privacyPolicyUrl?: string;
    termsOfServiceUrl?: string;
    emailNotifications?: {
        newFormSubmission?: boolean;
        newContact?: boolean;
        dailyAnalytics?: boolean;
    };
    cookieBannerSettings?: DoppeCookieBannerSettings;
    googleAnalyticsSettings?: DoppeGoogleAnalyticsSettings;
    showInDashboardOnboarding?: boolean;
    showCreateYourFirstPage?: boolean;
    showCreateYourFirstLinkNew?: boolean;
    showCreateYourFirstAction?: boolean;
    showCreateYourFirstLink?: boolean;
    showCreatedLinkDialog?: boolean;

    showPageActionsBanners?: HoppPageActionsBanners[];

    showConnectPageToLinksDialogAfterPublish?: boolean;
    showLinksCard?: boolean;
    showPreRollsCard?: boolean;
    showActionsPagesCard?: boolean;
    showHoppToCard?: boolean;
    customUTMSource?: DoppeDtoUserOnboardingUTMSource | null;
    customUTMSourceOtherText?: string | null;

    showPremiumOnboardingWizard?: boolean;
    userSelectedContactsMetaSite?: DoppeContactsMetaSite;
}

export type ZapierTrigger = {
    triggerUrl: string;
    triggerType: string;
    configuration?: AnyObject;
}

export interface DoppeDtoUserSettings extends DoppeDtoUserUpdatableSettings, DoppeDtoAppFeatures, DoppeDtoUserReadOnlySettings, DoppeDtoViewerFeatures {

}

export namespace DoppeDtoUserSettings {
    export type ForUpdate = DoppeDtoUserUpdatableSettings
}

const doppeUserSettingsDefaultValues = {
    showCreateYourFirstPage: false,
    showCreateYourFirstLinkNew: false,
    showInDashboardOnboarding: false,
    showCreateYourFirstAction: false,
    showCreatedLinkDialog: false,
    showConnectPageToLinksDialogAfterPublish: true,
    showCreateYourFirstLink: false,
    autoPublishPage: true,

    showLinksCard: false,
    showPreRollsCard: false,
    showActionsPagesCard: false,
    showPremiumOnboardingWizard: false,
    showHoppToCard: false,
    customUTMSource: null,
    customUTMSourceOtherText: null,

    showPageActionsBanners: DefaultPageActionsBanners,
    cookieBannerSettings: { cookieBannerEnabled: false, customTextSource: DoppeCookieBannerCustomTextSource.AutoValue},
    googleAnalyticsSettings: {},
    doppePartner: null,
    doppePartnerAccountId: null,
    leadData: null,
    premiumModelEnabled: false,

    contactsMetaSiteSelectionWasEnabledWhenAccountCreated: false,

    ...DoppeDtoAppFeaturesDefaultValues
} satisfies DoppeDtoUserSettings

export function doppeDtoUserSettingsShouldShowCreateYourFirstPage(userSettings: DoppeDtoUserSettings) {
    return userSettings.showCreateYourFirstPage ?? false;
}

export function doppeDtoUserSettingsShouldShowCreateYourFirstLinkNew(userSettings: DoppeDtoUserSettings) {
    return userSettings.showCreateYourFirstLinkNew ?? false;
}

export function doppeDtoUserSettingsShouldShowLinkCreatedDialog(userSettings: DoppeDtoUserSettings) {
    return userSettings.showCreatedLinkDialog ?? false;
}

export function doppeDtoUserSettingsGetSettingValue<K extends keyof DoppeDtoUserSettings>(
    userSettings: DoppeDtoUserSettings,
    settingName: K
) {
    return (
        userSettings[settingName] ??
        doppeUserSettingsDefaultValues[settingName as keyof typeof doppeUserSettingsDefaultValues]
    ) as K extends keyof typeof doppeUserSettingsDefaultValues ? Required<DoppeDtoUserSettings>[K] : DoppeDtoUserSettings[K]
}

export function doppeDtoUserSettingsExtendDefaultValues(
    userSettings: DoppeDtoUserSettings
) {
    return {
        ...doppeUserSettingsDefaultValues,
        ...userSettings
    } as PartiallyRequired<DoppeDtoUserSettings, keyof typeof doppeUserSettingsDefaultValues>
}

export function doppeDtoUserSettingsIsEmailNotificationEnabled(
    userSettings: DoppeDtoUserSettings,
    notificationType: keyof Required<DoppeDtoUserSettings>['emailNotifications']
) {
    return userSettings.emailNotifications?.[notificationType] ?? true;
}

export const DoppeDtoUserSettingsFieldsAssertions: ObjectFieldsAssertion<DoppeDtoUserSettings.ForUpdate> = {
    showLinkNotification: assert => assert.optional.isObject({
        shouldShow: assert => assert.isBoolean(),
        path: assert => assert.isString(doppeValidateLinkPath),
        targetUrl: assert => assert.isString(doppeValidateLinkUrl),
        linkId: assert => assert.isString()
    }),
    autoPublishPage: assert => assert.optional.isBoolean(),
    showInDashboardOnboarding: assert => assert.optional.isBoolean(),
    showCreateYourFirstPage: assert => assert.optional.isBoolean(),
    showCreateYourFirstLinkNew: assert => assert.optional.isBoolean(),
    showCreateYourFirstAction: assert => assert.optional.isBoolean(),
    showCreateYourFirstLink: assert => assert.optional.isBoolean(),
    showCreatedLinkDialog: assert => assert.optional.isBoolean(),

    showActionsPagesCard: assert => assert.optional.isBoolean(),
    showLinksCard: assert => assert.optional.isBoolean(),
    showPreRollsCard: assert => assert.optional.isBoolean(),
    showHoppToCard: assert => assert.optional.isBoolean(),
    customUTMSource: assert => assert.optional.isUnion(
        assert => assert.isNull(),
        assert => assert.isString()
    ),
    customUTMSourceOtherText: assert => assert.optional.isUnion(
        assert => assert.isNull(),
        assert => assert.isString()
    ),
    showConnectPageToLinksDialogAfterPublish: assert => assert.optional.isBoolean(),

    showPageActionsBanners: assert => assert.optional.isArray(
        assert => assert.isString()
    ),
    privacyPolicyUrl: assert => assert.optional.isString((value) => {
        return value.trim() === '' || doppeValidateReferencedExternalAssetUrl(value)
    }),
    termsOfServiceUrl: assert => assert.optional.isString((value) => {
        return value.trim() === '' || doppeValidateReferencedExternalAssetUrl(value)
    }),
    socialLinks: assert => assert.optional.isObject({
        instagram: assert => assert.optional.isString(doppeValidateReferencedExternalAssetUrl),
        facebook: assert => assert.optional.isString(doppeValidateReferencedExternalAssetUrl),
        patreon: assert => assert.optional.isString(doppeValidateReferencedExternalAssetUrl),
    }),
    emailNotifications: assert => assert.optional.isObject({
        newContact: assert => assert.optional.isBoolean(),
        newFormSubmission: assert => assert.optional.isBoolean(),
        dailyAnalytics: assert => assert.optional.isBoolean()
    }),
    googleAnalyticsSettings: assert => assert.optional.isObject({
            tagId: assert => assert.optional.isString(),
        },
    ),
    cookieBannerSettings: assert => assert.optional.isObject({
            cookieBannerEnabled: assert => assert.optional.isBoolean(),
            customTextSource: assert => assert.optional.isString(),
            customTexts: assert => assert.optional.isObject({
                consentButtonText: assert => assert.optional.isString(),
                settingsButtonText: assert => assert.optional.isString(),
                declineAllButtonText: assert => assert.optional.isString(),
                informText: assert => assert.optional.isString()
            })
        },
    ),

    showPremiumOnboardingWizard: assert => assert.optional.isBoolean(),
    userSelectedContactsMetaSite: assert => assert.optional.isUnion(
        assert => assert.isObject({
            metaSiteId: assert => assert.isString(),
        }),
        assert => assert.isString()
    ),
}